import React, { useContext, useEffect, useState } from 'react';
import { useRouteMatch, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import { InvoiceForm } from './InvoiceForm';
import { AuthContext } from '../../commons/auth/AuthContext';
import {
    fetchInvoiceData,
    fetchDraft,
    logInvoicingUserAgentInfo,
    sessionExpiryCheck,
    submitInvoiceDetails,
} from '../invoicingRoutes';
import LoadingPage from '../../commons/LoadingPage';
import { getPlatformInfo } from '../../commons/UserAgentInfo';
import { getUrlForTrackInvoicing } from '../../commons/UrlUtils';
import { useSessionExpiry } from '../../commons/auth/sessionExpiryHook';
import { PAYMENT_MODE } from '../InvoicingConstants';

const _prepareInvoicePayload = (formValues) => {
    let paymentDetails;

    const {
        consultantInvoiceBankDetails,
        consultantInvoiceWireTransferDetails,
        consultantInvoiceChequeDetails,
        callDuration,
        consultantRemarks,
        consultantInvoiceAdditionalChecks = {},
        paymentMode,
    } = formValues;

    if (paymentMode === PAYMENT_MODE.BANK_PAYMENT) {
        const {
            bankName,
            accountHolderName,
            accountNumber,
            bankRoutingNumber,
        } = consultantInvoiceBankDetails;
        paymentDetails = {
            consultantInvoiceBankDetails: {
                bankName,
                accountHolderName,
                accountNumber,
                bankRoutingNumber,
            },
        };
    } else if (paymentMode === PAYMENT_MODE.WIRE_TRANSFER) {
        const { bankName, accountHolderName, accountNumber, swiftCode } =
            consultantInvoiceWireTransferDetails;
        const { beneficiaryAddress } = consultantInvoiceWireTransferDetails;
        paymentDetails = {
            consultantInvoiceWireTransferDetails: {
                bankName,
                accountHolderName,
                accountNumber,
                swiftCode,
                ...(beneficiaryAddress && { beneficiaryAddress }),
            },
        };
    } else {
        const { beneficiaryAddress } = consultantInvoiceChequeDetails;
        paymentDetails = {
            consultantInvoiceChequeDetails: {
                ...(beneficiaryAddress && { beneficiaryAddress }),
            },
        };
    }

    return {
        callDuration,
        consultantRemarks,
        consultantInvoiceAdditionalChecks,
        paymentDetails,
    };
};

export const InvoiceFormContainer = (props) => {
    // State for invoice initial data from backend
    const [invoiceData, setInvoiceData] = useState();
    // State for saved draft data from backend
    const [draftData, setDraftData] = useState();
    const [invoiceSubmissionError, setFormSubmissionError] = useState();
    const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);

    const authContext = useContext(AuthContext);
    const routeMatch = useRouteMatch();
    const { url } = routeMatch.params;
    const history = useHistory();

    useSessionExpiry(url, authContext, sessionExpiryCheck);

    // Execute effect on first render to fetch both invoice data and draft data.
    // Invoice data contains the base/initial data from backend.
    // Draft data contains previously saved form progress
    useEffect(() => {
        fetchInvoiceData(url, authContext, setInvoiceData);
        fetchDraft(url, authContext, setDraftData);
        logInvoicingUserAgentInfo(
            url,
            getPlatformInfo('app.invoicingForm'),
            authContext
        ).catch(console.error);
    }, []);

    // directly submitting the details to backend
    const _submitInvoice = (formValues) => {
        const invoicePayload = _prepareInvoicePayload(formValues);
        setInvoiceSubmitting(true);
        setFormSubmissionError(null);
        submitInvoiceDetails(url, invoicePayload, authContext)
            .then(() => {
                setInvoiceSubmitting(false);
                history.push(getUrlForTrackInvoicing(url), {
                    redirectAfterInvoiceSubmission: true,
                });
            })
            .catch((error) => {
                setInvoiceSubmitting(false);
                if (error?.response?.status !== 401) {
                    // set submission error only if it is not a result of 401 expired token call.
                    // as user would be displayed SessionExpired popup to re-login and try again.
                    setFormSubmissionError(
                        'Error submitting invoice details. Please try again later.'
                    );
                }
            });
    };

    if (!invoiceData) return <LoadingPage />;

    // Merge invoice data with draft data. Draft data (if present) should override invoice data to restore the user's previously saved progress.
    const mergedInitialValues = {
        ...invoiceData,
        ...draftData?.values,
    };

    return (
        <div className="max-w-4xl mx-auto bg-white px-2 py-4 md:mt-6 md:mb-6 rounded shadow-md text-sm">
            {invoiceSubmitting && (
                <LoadingPage message={'Submitting Invoice...'} />
            )}
            <InvoiceForm
                initialValues={mergedInitialValues}
                onSubmit={_submitInvoice}
                url={url}
                submissionError={invoiceSubmissionError}
                initialPageNumber={props.initialPageNumber}
            />
        </div>
    );
};

InvoiceFormContainer.propTypes = {
    initialPageNumber: PropTypes.number,
};

export default InvoiceFormContainer;
